/**
 * The External (MetaMask / Trust Wallet) withdrawal pipeline's non-visual
 * half, mirroring `features/auth/wallet/signInFlow.ts`: every browser piece
 * (the connector, the provider, Axios) is injected, so the guarantees that
 * matter on this money path — exactly one blockchain transaction per attempt,
 * no Confirm before real on-chain success, indexing-delay retries that never
 * touch the transaction — are tested directly against fake deps rather than
 * inferred from a rendered dialog.
 *
 * WHAT THIS FILE DOES NOT DO. It never calls `submitWithdraw` (the initial
 * withdraw request happens exactly once, before this flow starts, in the
 * dialog) and it never reconstructs `sign_payload.transaction` — that object
 * is sent to `eth_sendTransaction` verbatim (see the module header on
 * `WithdrawSignPayload` in `types.ts`).
 */

import type { WithdrawSignPayload } from "./types";

export type ExternalWithdrawStatus =
  | "validating_wallet_account"
  | "validating_network"
  | "awaiting_wallet_signature"
  | "transaction_submitted"
  | "waiting_for_receipt"
  | "confirming_backend"
  | "retrying_same_confirmation"
  | "done";

/** The connected account did not match `sign_payload.from`. Never auto-resolved. */
export class AccountMismatchError extends Error {
  expected: string;
  actual: string;
  constructor(expected: string, actual: string) {
    super("account_mismatch");
    this.name = "AccountMismatchError";
    this.expected = expected;
    this.actual = actual;
  }
}

/** The wallet's chain still did not match after a switch attempt. */
export class NetworkMismatchError extends Error {
  expectedChainId: number;
  constructor(expectedChainId: number) {
    super("network_mismatch");
    this.name = "NetworkMismatchError";
    this.expectedChainId = expectedChainId;
  }
}

/** The submitted transaction reached a final on-chain state other than success. */
export class TransactionFailedError extends Error {
  trxHash: string;
  status: number | null;
  constructor(trxHash: string, status: number | null) {
    super("transaction_failed");
    this.name = "TransactionFailedError";
    this.trxHash = trxHash;
    this.status = status;
  }
}

export type ExternalWithdrawResult =
  | { outcome: "done"; trxHash: string; confirmResponse: unknown }
  /**
   * The transaction succeeded on-chain and Confirm was retried the bounded
   * number of times, but the backend is still indexing it. NOT a failure:
   * `withdrawId`/`trxHash` stay valid and a caller may retry Confirm again
   * later with the exact same pair — see §11.
   */
  | { outcome: "pending_confirmation"; trxHash: string; withdrawId: string | number; lastConfirmError: unknown };

export type ExternalWithdrawDeps = {
  signPayload: WithdrawSignPayload;
  /** Live re-read of the wallet's current selection — never a cached value. */
  readAccount: () => Promise<{ address: string; chainId: string }>;
  /** Throws on rejection/failure. The caller re-reads the chain to verify. */
  switchNetwork: (chainId: number) => Promise<void>;
  /** `eth_sendTransaction` with the payload's own transaction object. Called at most once. */
  sendTransaction: (transaction: Record<string, unknown>) => Promise<string>;
  /** Resolves once the tx has a final receipt. `status`: 1 success, 0 revert, null if indeterminate. */
  waitForReceipt: (trxHash: string) => Promise<{ status: number | null }>;
  confirmWithdraw: (payload: { withdraw_id: string | number; trx_hash: string }) => Promise<unknown>;
  onStatus?: (status: ExternalWithdrawStatus) => void;
  /** Injectable for tests — real callers omit and get a real timer. */
  delay?: (ms: number) => Promise<void>;
  maxConfirmAttempts?: number;
  confirmRetryDelayMs?: number;
};

export const DEFAULT_MAX_CONFIRM_ATTEMPTS = 5;
export const DEFAULT_CONFIRM_RETRY_DELAY_MS = 4000;

/** Case-insensitive: EIP-55 checksummed and lowercase forms are the same account. */
export function isSameAddress(a: string | null | undefined, b: string | null | undefined): boolean {
  return Boolean(a) && Boolean(b) && a!.toLowerCase() === b!.toLowerCase();
}

/** `walletChainId` is the decimal-string shape `connectors.ts` already normalises to. */
export function chainIdMatches(walletChainId: string, expectedChainId: number): boolean {
  const parsed = Number.parseInt(walletChainId, 10);
  return Number.isFinite(parsed) && parsed === expectedChainId;
}

function extractErrorMessage(error: unknown): string {
  const data = (error as { response?: { data?: { message?: unknown; errors?: unknown } } } | null | undefined)
    ?.response?.data;
  if (data) {
    if (typeof data.message === "string" && data.message) return data.message;
    const errs = data.errors;
    if (typeof errs === "string") return errs;
    if (Array.isArray(errs) && typeof errs[0] === "string") return errs[0];
  }
  const message = (error as { message?: unknown } | null | undefined)?.message;
  return typeof message === "string" ? message : "";
}

/**
 * ONLY the backend's own "haven't indexed this tx yet" condition qualifies —
 * matched on wording, since `meta.code` (the machine-readable signal used
 * elsewhere in this API, see `features/auth/wallet/types.ts`) is not
 * documented for this endpoint. A real Confirm failure (wrong hash, already
 * confirmed, unrelated validation) must not contain any of these phrases and
 * so falls through to the non-retryable path.
 */
const INDEXING_DELAY_PATTERN = /not\s*(?:yet\s*)?(?:found|confirm(?:ed)?|indexed)/i;

export function isRetryableConfirmError(error: unknown): boolean {
  return INDEXING_DELAY_PATTERN.test(extractErrorMessage(error));
}

/**
 * One attempt, start to finish: validate the connected account, validate (and
 * if needed switch) the network, sign and send the transaction EXACTLY ONCE,
 * wait for its on-chain result, then Confirm — retrying Confirm alone, with
 * the same `withdraw_id`/`trx_hash`, for the documented indexing delay.
 *
 * Throws `AccountMismatchError` / `NetworkMismatchError` / `TransactionFailedError`
 * for the hard-stop cases (§6, §7, §9); anything `sendTransaction` or
 * `switchNetwork` itself throws (a wallet rejection, an RPC error) propagates
 * unchanged — this function never invents a friendlier error in its place.
 */
export async function runExternalWithdraw(deps: ExternalWithdrawDeps): Promise<ExternalWithdrawResult> {
  const { signPayload } = deps;
  const delay = deps.delay ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
  const maxAttempts = deps.maxConfirmAttempts ?? DEFAULT_MAX_CONFIRM_ATTEMPTS;
  const retryDelayMs = deps.confirmRetryDelayMs ?? DEFAULT_CONFIRM_RETRY_DELAY_MS;

  deps.onStatus?.("validating_wallet_account");
  let current = await deps.readAccount();
  if (!isSameAddress(current.address, signPayload.from)) {
    throw new AccountMismatchError(signPayload.from, current.address);
  }

  deps.onStatus?.("validating_network");
  if (!chainIdMatches(current.chainId, signPayload.chain_id)) {
    await deps.switchNetwork(signPayload.chain_id);
    current = await deps.readAccount();
    if (!chainIdMatches(current.chainId, signPayload.chain_id)) {
      throw new NetworkMismatchError(signPayload.chain_id);
    }
    // The wallet's account picker is reachable from the same UI as its
    // network switcher; re-asserting here catches a user who changed both.
    if (!isSameAddress(current.address, signPayload.from)) {
      throw new AccountMismatchError(signPayload.from, current.address);
    }
  }

  deps.onStatus?.("awaiting_wallet_signature");
  const trxHash = await deps.sendTransaction(signPayload.transaction);
  deps.onStatus?.("transaction_submitted");

  deps.onStatus?.("waiting_for_receipt");
  const receipt = await deps.waitForReceipt(trxHash);
  if (receipt.status !== 1) {
    throw new TransactionFailedError(trxHash, receipt.status);
  }

  deps.onStatus?.("confirming_backend");
  let lastError: unknown = null;
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      const confirmResponse = await deps.confirmWithdraw({
        withdraw_id: signPayload.withdraw_id,
        trx_hash: trxHash,
      });
      deps.onStatus?.("done");
      return { outcome: "done", trxHash, confirmResponse };
    } catch (error) {
      lastError = error;
      if (!isRetryableConfirmError(error) || attempt === maxAttempts) break;
      deps.onStatus?.("retrying_same_confirmation");
      await delay(retryDelayMs);
    }
  }

  if (isRetryableConfirmError(lastError)) {
    return {
      outcome: "pending_confirmation",
      trxHash,
      withdrawId: signPayload.withdraw_id,
      lastConfirmError: lastError,
    };
  }
  throw lastError;
}

/**
 * Retries Confirm alone for a withdrawal already sitting in
 * `pending_confirmation` — e.g. from a "check again" action reached after the
 * dialog was reopened. Never sends a transaction; there is nothing here to
 * send. Shares the same bounded-retry shape as the tail of `runExternalWithdraw`.
 */
export async function retryPendingConfirmation(deps: {
  withdrawId: string | number;
  trxHash: string;
  confirmWithdraw: (payload: { withdraw_id: string | number; trx_hash: string }) => Promise<unknown>;
  onStatus?: (status: ExternalWithdrawStatus) => void;
  delay?: (ms: number) => Promise<void>;
  maxConfirmAttempts?: number;
  confirmRetryDelayMs?: number;
}): Promise<ExternalWithdrawResult> {
  const delay = deps.delay ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
  const maxAttempts = deps.maxConfirmAttempts ?? DEFAULT_MAX_CONFIRM_ATTEMPTS;
  const retryDelayMs = deps.confirmRetryDelayMs ?? DEFAULT_CONFIRM_RETRY_DELAY_MS;

  deps.onStatus?.("confirming_backend");
  let lastError: unknown = null;
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      const confirmResponse = await deps.confirmWithdraw({
        withdraw_id: deps.withdrawId,
        trx_hash: deps.trxHash,
      });
      deps.onStatus?.("done");
      return { outcome: "done", trxHash: deps.trxHash, confirmResponse };
    } catch (error) {
      lastError = error;
      if (!isRetryableConfirmError(error) || attempt === maxAttempts) break;
      deps.onStatus?.("retrying_same_confirmation");
      await delay(retryDelayMs);
    }
  }

  if (isRetryableConfirmError(lastError)) {
    return { outcome: "pending_confirmation", trxHash: deps.trxHash, withdrawId: deps.withdrawId, lastConfirmError: lastError };
  }
  throw lastError;
}
